import { useMemo } from "react";
import { useFormik } from "formik";
import { Flex, FormControl, FormLabel, Select as ChakraSelect, Input as ChakraInput, Button } from "@chakra-ui/react";
import { RootState, useAppSelector } from "@/store";
import { LeadsFilter } from "@/model/lead";
import { defaultLeadsFilterFields } from "../_helpers";
import { Recruiter } from "@/model/recruiters";

export interface LeadsPopupFilterProps {
  readonly values: LeadsFilter;
  readonly onApply: (values: LeadsFilter) => void;
  readonly onReset: () => void;
  readonly recruiters: readonly Recruiter[];
}

export const LeadsPopupFilter = ({ values, onApply, onReset, recruiters }: LeadsPopupFilterProps) => {
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const formik = useFormik<LeadsFilter>({
    initialValues: values,
    enableReinitialize: true,
    onSubmit: (submittedValues: LeadsFilter) => {
      onApply(submittedValues);
    },
  });

  const statusOptions = useMemo(() => {
    const map: Record<string, string> | undefined = (guides as any)?.LeadStatus;
    if (!map) return [] as { value: string; label: string }[];
    return Object.entries(map).map(([value, label]) => ({ value, label: String(label) }));
  }, [guides]);

  const sourceOptions = useMemo(() => {
    const map: Record<string, string> | undefined = (guides as any)?.LeadSource;
    if (!map) return [] as { value: string; label: string }[];
    return Object.entries(map).map(([value, label]) => ({ value, label: String(label) }));
  }, [guides]);

  const handleResetLocal = () => {
    formik.resetForm({ values: defaultLeadsFilterFields });
    onReset();
  };

  const handleApplyLocal = () => {
    void formik.submitForm();
  };

  return (
    <>
      <FormControl>
        <FormLabel>Статус</FormLabel>
        <ChakraSelect
          placeholder="Все статусы"
          value={formik.values.status_id || ""}
          onChange={(e) => {
            formik.setFieldValue("status_id", e.target.value || "");
          }}
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Источник</FormLabel>
        <ChakraSelect
          placeholder="Все источники"
          value={formik.values.source_id || ""}
          onChange={(e) => {
            formik.setFieldValue("source_id", e.target.value || "");
          }}
        >
          {sourceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Ответственный</FormLabel>
        <ChakraSelect
          placeholder="Все сотрудники"
          value={formik.values.responsible_user_id || ""}
          onChange={(e) => {
            formik.setFieldValue("responsible_user_id", e.target.value || "");
          }}
        >
          {recruiters.map((r) => (
            <option key={r.id} value={String(r.id)}>
              {r.username}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Дата создания</FormLabel>
        <Flex direction="row" gap="12px">
          <ChakraInput
            type="date"
            value={formik.values.created_at_from || ""}
            placeholder="От"
            onChange={(e) => formik.setFieldValue("created_at_from", e.target.value || "")}
          />
          <ChakraInput
            type="date"
            value={formik.values.created_at_to || ""}
            placeholder="До"
            onChange={(e) => formik.setFieldValue("created_at_to", e.target.value || "")}
          />
        </Flex>
      </FormControl>
      <Flex justifyContent="flex-end" gap="8px">
        <Button variant="ghost" onClick={handleResetLocal}>
          Сбросить
        </Button>
        <Button onClick={handleApplyLocal} bg="#2563eb" color="white" _hover={{ bg: "#2563eb" }} _active={{ bg: "#2563eb" }}>
          Применить
        </Button>
      </Flex>
    </>
  );
};
